import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControlLabel,
  Checkbox as MuiCheckbox,
} from "@mui/material";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  Input,
  FileUpload,
  Button,
} from "src/components/common/FormComponents";
import Checkbox from "src/components/common/FormComponents/Checkbox";
import { getStyles } from "src/styles/theme";
import defaultStyles, {
  StylesClasses,
} from "src/components/feature/ProjectDetailView/styles";
import {
  UpdateSiteVisitFormValues,
  defaultUpdateSiteVisitValues,
} from "./types";
import { useMutation, useQueryClient } from "react-query";
import {
  submitSiteVisitDetails,
  submitSiteVisitDocs,
} from "src/utils/api/siteVisit";
import {
  getProjectDetailsById,
  getProjectOptionsDetails,
  getLandDetailsById,
} from "src/utils/api/createProjectFlow";
import { getProjectById } from "src/utils/api/dashboard";
import ProjectOverview from "src/components/common/ProjectOverview";
import CharacterCounter, {
  isCharacterLimitReached,
} from "src/components/common/CharacterCounter";

const REMARKS_MAX_LENGTH = 5000;
const UpdateSiteVisitDetails = (): JSX.Element => {
  const styles = getStyles<StylesClasses>(defaultStyles);
  const [projectData, setProjectData] = useState<any>(null);
  const [landOwnershipId, setLandOwnershipId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [projectDataByEpid, setProjectDataByEpid] = useState<any>(null);

  const [pendingFormData, setPendingFormData] =
    useState<UpdateSiteVisitFormValues | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
    isUploading: boolean;
  }>({
    current: 0,
    total: 0,
    isUploading: false,
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    trigger,
    formState: { errors },
  } = useForm<UpdateSiteVisitFormValues>({
    defaultValues: defaultUpdateSiteVisitValues,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "siteVisitedByEntity",
  });

  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (vars: {
      landOwnershipId: string;
      projectId: string;
      payload: UpdateSiteVisitFormValues;
    }) => {
      const { landOwnershipId, payload } = vars as any;

      try {
        const fileIds = (payload?.attachments || []).map((file) => file.key);

        const siteVisitResponse = await submitSiteVisitDetails(
          landOwnershipId || "",
          {
            ...payload,
            fileIds,
          }
        );
        return { success: true, documentsUploaded: fileIds.length };
      } catch (error) {
        throw error;
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("projects");
        setUploadProgress({ current: 0, total: 0, isUploading: false });
        // Refetch the land details to show updated siteVisitDocs
        if (epid) {
          fetchProjectOptionsDetails();
        }
      },
      onError: (err: any) => {
        console.error(err);
        setUploadProgress({ current: 0, total: 0, isUploading: false });
      },
    }
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const epid = searchParams.get("id") ?? undefined;
  useEffect(() => {
    if (epid) {
      fetchProjectOptionsDetails();
    }
  }, [epid]);

  const onSubmit = (data: UpdateSiteVisitFormValues) => {
    // Store form data and show confirmation dialog
    setPendingFormData(data);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = () => {
    if (!landOwnershipId || !projectData?.id || !pendingFormData) {
      console.error(
        "Land ownership ID, Project ID, or Form data is not available"
      );
      return;
    }
    setShowConfirmDialog(false);
    mutation.mutate({
      landOwnershipId,
      projectId: projectData?.id,
      payload: pendingFormData,
    });
  };

  const handleCancelSubmit = () => {
    setShowConfirmDialog(false);
    setPendingFormData(null);
  };

  async function fetchProjectOptionsDetails() {
    try {
      // First resolve land-ownership details (EPID) to get the internal project id
      const landRes = await getLandDetailsById(epid as string);
      setProjectDataByEpid(landRes?.data);
      const landOwnershipId = landRes?.data?.id;
      const resolvedProjectId =
        landRes?.data?.projectDetail?.id || landRes?.data?.projectDetail;

      if (!resolvedProjectId) {
        console.warn(
          "Could not resolve project id from land details:",
          landRes
        );
        return;
      }

      // Store land ownership ID for site visit details submission
      if (landOwnershipId) {
        setLandOwnershipId(landOwnershipId);
      }

      const details = await getProjectById(resolvedProjectId);
      setProjectData(details.data);
    } catch (error) {
      console.error("Error fetching project options details:", error);
    }
  }

  const siteVisitConfirmation = watch("siteVisitConfirmation");
  const siteDeselect = watch("siteDeselect");
  const siteVisitConfirmationRemarks = watch("siteVisitConfirmationRemarks");
  const siteDeselectReason = watch("siteDeselectReason");
  const isRemarksLimitReached = isCharacterLimitReached(
    siteVisitConfirmationRemarks,
    REMARKS_MAX_LENGTH
  );
  const isDeselectReasonLimitReached = isCharacterLimitReached(
    siteDeselectReason,
    REMARKS_MAX_LENGTH
  );

  // Compute today's date in local timezone (YYYY-MM-DD) for the date picker max
  const todayLocalDate = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  })();

  return (
    <Box
      sx={{
        maxWidth: 1000,
        margin: "24px auto",
        p: 3,
        bgcolor: "background.paper",
        borderRadius: 2,
      }}
    >
      {projectData && (
        <ProjectOverview
          projectData={projectData}
          projectDataByEpid={projectDataByEpid}
        />
      )}

      {projectDataByEpid?.status === "PROVISIONAL_SELECTED" && (
        <Box {...styles("formWrapper")} style={{ minWidth: "100%" }}>
          <Box {...styles("heading")}>Update site visit details</Box>
          <Typography variant="h5" sx={{ mb: 1 }}></Typography>
          <Typography variant="body2" sx={{ color: "red", mb: 3 }}>
            Fill in the details after selecting a land parcel under the Green
            Credit Programme (GCP).
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ mb: 2 }}>
              <Input
                name="siteVisitFirstDate"
                label="Date of site visit"
                type="date"
                control={control}
                rules={{
                  required: "Date of site visit is required",
                  validate: (value) =>
                    !value ||
                    (value as string) <= todayLocalDate ||
                    "Date of site visit cannot be in the future",
                }}
                inputProps={{
                  max: todayLocalDate,
                }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                Entity representative(s) who visited the site (or) Deselected
                the site
              </Typography>
              {fields.map((field, idx) => (
                <Box
                  key={field.id}
                  sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Input
                      name={`siteVisitedByEntity.${idx}.name`}
                      label="Name"
                      control={control}
                      rules={{ required: "Name is required" }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Input
                      name={`siteVisitedByEntity.${idx}.designation`}
                      label="Designation"
                      control={control}
                      rules={{ required: "Designation is required" }}
                    />
                  </Box>
                  <Box>
                    <Button
                      text="Remove"
                      variant="outlined"
                      disabled={fields.length === 1}
                      onClick={() => remove(idx)}
                      customStyles={{
                        root: {
                          bgcolor: "error.light",
                          color: "error.contrastText",
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
              <Box sx={{ mt: 2 }}>
                <Button
                  text="Add representative"
                  onClick={() => append({ name: "", designation: "" })}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Provide name and designation for each person who attended the
                visit. You may add multiple representatives.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                Based on the field visit
              </Typography>
              <Controller
                name="siteVisitConfirmation"
                control={control}
                rules={{
                  validate: () =>
                    getValues("siteVisitConfirmation") ||
                    getValues("siteDeselect") ||
                    "Please select one of the two options above",
                }}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <MuiCheckbox
                        checked={!!field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (e.target.checked) {
                            setValue("siteDeselect", false as any);
                            setValue("siteDeselectReason", "");
                          }
                          trigger(["siteVisitConfirmation", "siteDeselect"]);
                        }}
                      />
                    }
                    label="We hereby confirm our participation for restoration of the above-mentioned land parcel"
                  />
                )}
              />
            </Box>

            {siteVisitConfirmation && (
              <Box sx={{ mb: 2, borderBottom: 1, pb: 2 }}>
                <Input
                  name="siteVisitConfirmationRemarks"
                  label="Remarks (if any)"
                  multiline
                  minRows={3}
                  maxLength={REMARKS_MAX_LENGTH}
                  control={control}
                  rules={{
                    validate: (value) =>
                      !getValues("siteVisitConfirmation") ||
                      !!(value as string)?.trim() ||
                      "Remarks are required when confirming participation",
                  }}
                  customStyles={
                    isRemarksLimitReached
                      ? { input: { borderColor: "error.main" } }
                      : undefined
                  }
                />
                <CharacterCounter
                  value={siteVisitConfirmationRemarks}
                  maxLength={REMARKS_MAX_LENGTH}
                />
              </Box>
            )}

            <Box sx={{ mb: 1 }}>
              <Typography {...styles("heading")}>
                If you want to deselect the site
              </Typography>
              <Controller
                name="siteDeselect"
                control={control}
                rules={{
                  validate: () =>
                    getValues("siteVisitConfirmation") ||
                    getValues("siteDeselect") ||
                    "Please select one of the two options above",
                }}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <MuiCheckbox
                        checked={!!field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          if (e.target.checked) {
                            setValue("siteVisitConfirmation", false);
                            setValue("siteVisitConfirmationRemarks", "");
                          }
                          trigger(["siteDeselect", "siteVisitConfirmation"]);
                        }}
                      />
                    }
                    label="We hereby voluntarily request deselection of the selected site/block and acknowledge that the same may be made available for selection by other eligible entities through the GCP Portal as per applicable guidelines."
                  />
                )}
              />
              {siteDeselect && (
                <Box sx={{ mt: 1 }}>
                  <Input
                    name="siteDeselectReason"
                    label="Reason for deselecting the site"
                    multiline
                    minRows={3}
                    maxLength={REMARKS_MAX_LENGTH}
                    control={control}
                    rules={{
                      validate: (value) =>
                        !getValues("siteDeselect") ||
                        !!(value as string)?.trim() ||
                        "Reason for deselection is required",
                    }}
                    customStyles={
                      isDeselectReasonLimitReached
                        ? { input: { borderColor: "error.main" } }
                        : undefined
                    }
                  />
                  <CharacterCounter
                    value={siteDeselectReason}
                    maxLength={REMARKS_MAX_LENGTH}
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <FileUpload
                name="attachments"
                label="Upload Geotagged Photos (2 Photos are mandatory) *"
                control={control}
                errors={errors}
                maxLimit={10}
                supportedFormatMessage="Accepted: images (jpg, png, webp) and PDF."
                setValue={setValue}
                getValues={getValues}
                setError={setError}
                clearErrors={clearErrors}
                trigger={trigger}
                rules={{
                  validate: (value) =>
                    (Array.isArray(value) && value.length >= 2) ||
                    "At least 2 geotagged photos are required",
                }}
              />
            </Box>

            {uploadProgress.isUploading && (
              <Box sx={{ mb: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Uploading documents: {uploadProgress.current}/
                  {uploadProgress.total}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    height: 8,
                    bgcolor: "info.lighter",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${
                        (uploadProgress.current / uploadProgress.total) * 100
                      }%`,
                      bgcolor: "info.main",
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
              }}
            >
              {(mutation.isLoading || uploadProgress.isUploading) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ color: "info.main" }}>
                    {uploadProgress.isUploading
                      ? `Uploading... ${uploadProgress.current}/${uploadProgress.total}`
                      : "Submitting..."}
                  </Typography>
                </Box>
              )}
              <Button
                type="submit"
                text={
                  mutation.isLoading || uploadProgress.isUploading
                    ? "Submitting..."
                    : "Submit"
                }
                disabled={mutation.isLoading || uploadProgress.isUploading}
              />
            </Box>
          </Box>
        </Box>
      )}
      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelSubmit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirm Site Visit Details Submission
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" fontWeight={600}>
            Are you sure you want to submit the site visit details? This action
            will save all the information you've entered along with the uploaded
            documents.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            text="Cancel"
            variant="outlined"
            onClick={handleCancelSubmit}
          />
          <Button
            text="Submit"
            variant="contained"
            onClick={handleConfirmSubmit}
            disabled={mutation.isLoading}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdateSiteVisitDetails;
